/**
 * useConsent — React hook exposing the analytics consent state.
 *
 * Card LeZvSY6e — OBS F0.4 Consent module.
 *
 * Reads the persisted record from `lib/analytics/consent`, subscribes to
 * the `consent-changed` `CustomEvent` dispatched by `setConsent()` /
 * `clearConsent()` (same tab), and also listens for the browser `storage`
 * event so a `setConsent()` in one tab propagates to sibling tabs.
 *
 * Returns `{ consent, setConsent }`. The setter is a thin wrapper over
 * the pure `setConsent()` from `lib/analytics/consent` — provided as a
 * stable reference so consumers can pass it into effect deps without
 * re-running.
 */
import { useCallback, useEffect, useState } from 'react';

import {
  CONSENT_CHANGED_EVENT,
  type Consent,
  type ConsentInput,
  getConsent,
  setConsent as persistConsent,
} from '@/lib/analytics/consent';

export interface UseConsentResult {
  /**
   * Current persisted consent, or `null` when nothing has been captured
   * yet OR the persisted contract version is stale. Callers gating side
   * effects on consent should check `consent?.analytics === 'granted'`
   * rather than truthiness — `null` MUST be treated as denial.
   */
  consent: Consent | null;
  /** Persist a new consent record. Fires `consent-changed` under the hood. */
  setConsent: (input: ConsentInput) => void;
}

export function useConsent(): UseConsentResult {
  const [consent, setLocalConsent] = useState<Consent | null>(() => getConsent());

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleConsentChanged = (event: Event): void => {
      const detail = (event as CustomEvent<Consent | null>).detail;
      // `detail` is authoritative for same-tab dispatches (setConsent /
      // clearConsent stamp it themselves). We still round-trip through
      // `getConsent()` when detail is missing so a manual
      // `dispatchEvent(new Event('consent-changed'))` from a caller
      // outside this module keeps working.
      if (detail === null || detail === undefined) {
        setLocalConsent(getConsent());
      } else {
        setLocalConsent(detail);
      }
    };

    const handleStorage = (event: StorageEvent): void => {
      // Cross-tab: the browser fires `storage` in every OTHER tab when
      // localStorage changes. Same-tab dispatch is covered by the
      // CustomEvent listener above.
      if (event.key === null || event.key.startsWith('inbest.consent.')) {
        setLocalConsent(getConsent());
      }
    };

    window.addEventListener(CONSENT_CHANGED_EVENT, handleConsentChanged);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener(CONSENT_CHANGED_EVENT, handleConsentChanged);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  const setConsent = useCallback((input: ConsentInput): void => {
    persistConsent(input);
    // `persistConsent` dispatches `consent-changed` synchronously — the
    // subscription above will pick it up and update state. No need to
    // call `setLocalConsent` here (would cause a double render).
  }, []);

  return { consent, setConsent };
}
